<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, formatDisplayDateTime } from '@yuksalish/ui'
import { useFinanceApi } from '../composables/useFinanceApi'
import { accountTypeLabel, entryKindLabel, formatMoney } from '../utils/format'
import type { FinancialAccount, TreasuryTransaction } from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()
const { getFinancialAccount, listAccountTransactions } = useFinanceApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('financial_accounts.manage'))

const accountId = computed(() => String(route.params.id ?? ''))
const account = ref<FinancialAccount | null>(null)
const transactions = ref<TreasuryTransaction[]>([])
const loading = ref(true)
const error = ref('')
const activeTab = ref<'ledger' | 'summary'>('ledger')

const totals = computed(() => {
  let inflow = 0
  let outflow = 0
  for (const transaction of transactions.value) {
    if (transaction.direction === 'inflow') inflow += Number(transaction.amount ?? 0)
    else outflow += Number(transaction.amount ?? 0)
  }
  return { inflow, outflow, net: inflow - outflow }
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [detail, ledger] = await Promise.all([
      getFinancialAccount(accountId.value),
      listAccountTransactions(accountId.value),
    ])
    account.value = detail
    transactions.value = [...ledger].sort((a, b) =>
      String(b.transaction_date ?? '').localeCompare(String(a.transaction_date ?? '')),
    )
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/treasury/accounts">← {{ t('finance.accounts.title') }}</RouterLink>
    <h2>{{ account?.name || t('finance.accounts.title') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else-if="account">
    <div class="toolbar">
      <div class="button-group">
        <RouterLink
          v-if="canManage"
          class="button-group__item"
          :to="`/treasury/accounts/${account.id}/edit`"
        >
          {{ t('finance.common.edit') }}
        </RouterLink>
        <button class="button-group__item" :disabled="loading" @click="load">
          {{ t('finance.common.refresh') }}
        </button>
      </div>
      <span class="spacer" />
      <div class="summary">
        <span>
          <strong>{{ formatMoney(account.current_balance, account.currency_code) }}</strong>
          {{ t('finance.common.currentBalance') }}
        </span>
      </div>
    </div>

    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.common.openingBalance') }}</span>
        <span class="summary-value">{{ formatMoney(account.opening_balance, account.currency_code) }}</span>
        <span class="summary-label">{{ formatDisplayDate(account.opening_balance_date) }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.common.currentBalance') }}</span>
        <span class="summary-value">{{ formatMoney(account.current_balance, account.currency_code) }}</span>
        <span class="summary-label">{{ accountTypeLabel(t, account.account_type) }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('finance.common.payments') }}</span>
        <span class="summary-value">{{ formatMoney(totals.net, account.currency_code) }}</span>
        <span class="summary-label">
          +{{ formatMoney(totals.inflow, account.currency_code) }} /
          −{{ formatMoney(totals.outflow, account.currency_code) }}
        </span>
      </div>
    </div>

    <div class="tabs">
      <button
        class="tab-button"
        :class="{ 'is-active': activeTab === 'ledger' }"
        @click="activeTab = 'ledger'"
      >
        {{ t('finance.accounts.transactions') }}
      </button>
      <button
        class="tab-button"
        :class="{ 'is-active': activeTab === 'summary' }"
        @click="activeTab = 'summary'"
      >
        {{ t('finance.common.detail') }}
      </button>
    </div>

    <div class="tab-block">
      <div v-if="activeTab === 'summary'" class="panel">
        <table class="data-table compact">
          <tbody>
            <tr>
              <td class="field-label">{{ t('finance.accounts.accountCode') }}</td>
              <td class="field-value">{{ account.account_code }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.accounts.accountType') }}</td>
              <td class="field-value">{{ accountTypeLabel(t, account.account_type) }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.common.currency') }}</td>
              <td class="field-value">{{ account.currency_code }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.accounts.department') }}</td>
              <td class="field-value">{{ account.department_name || '—' }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.accounts.branch') }}</td>
              <td class="field-value">{{ account.branch_name || '—' }}</td>
            </tr>
            <tr>
              <td class="field-label">{{ t('finance.common.status') }}</td>
              <td class="field-value">
                {{ account.is_active === 1 ? t('finance.common.active') : t('finance.common.inactive') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else>
        <div v-if="transactions.length" class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>{{ t('finance.common.date') }}</th>
                <th>{{ t('finance.accounts.entryKind') }}</th>
                <th>{{ t('finance.common.direction') }}</th>
                <th>{{ t('finance.common.reference') }}</th>
                <th>{{ t('finance.common.status') }}</th>
                <th class="cell-num">{{ t('finance.common.amount') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in transactions" :key="entry.id">
                <td class="cell-date">{{ formatDisplayDate(entry.transaction_date) }}</td>
                <td class="cell-name">{{ entryKindLabel(t, entry.entry_kind) }}</td>
                <td>{{ t(`finance.paymentDirection.${entry.direction}`) }}</td>
                <td>{{ entry.reference || entry.description || '—' }}</td>
                <td>
                  <span class="status-badge" :class="entry.status === 'posted' ? 'status-ok' : 'status-critical'">
                    {{ entry.status === 'posted' ? t('finance.transferStatus.posted') : t('finance.transferStatus.reversed') }}
                  </span>
                  <span class="cell-sub">{{ formatDisplayDateTime(entry.posted_at) }}</span>
                </td>
                <td
                  class="cell-num"
                  :class="entry.direction === 'inflow' ? 'is-positive' : 'is-negative'"
                >
                  {{ entry.direction === 'inflow' ? '+' : '−'
                  }}{{ formatMoney(entry.amount, entry.currency_code) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="empty-inline">{{ t('finance.accounts.noTransactions') }}</p>
      </div>
    </div>
  </template>
</template>
